/**
 * Footer Component
 * 
 * A modern, responsive footer that displays site copyright, quick links,
 * and language switcher. Features a clean design with subtle animations.
 * 
 * Features:
 * - Responsive layout that adapts to different screen sizes
 * - Animated entrance for visual elements
 * - i18n support for all text content
 * - Social media links with hover effects
 * - Year automatically updates
 */

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useTranslation } from 'next-i18next';
import { motion } from "framer-motion";
import { 
  Github, 
  Twitter, 
  Facebook, 
  Instagram,
  Heart
} from 'lucide-react';

import LanguageSwitcher from "../common/LanguageSwitcher";

// Quick links structure
const QUICK_LINKS = [
  { href: "/copyright", key: "copyright" },
  { href: "/terms", key: "terms" },
  { href: "/privacy", key: "privacy" },
];

// Social media links
const SOCIAL_LINKS = [
  { href: "https://github.com", icon: <Github className="w-5 h-5" />, label: "GitHub" },
  { href: "https://twitter.com", icon: <Twitter className="w-5 h-5" />, label: "Twitter" },
  { href: "https://facebook.com", icon: <Facebook className="w-5 h-5" />, label: "Facebook" },
  { href: "https://instagram.com", icon: <Instagram className="w-5 h-5" />, label: "Instagram" },
];

const Footer: React.FC = () => {
  const { t } = useTranslation('footer');
  const currentYear = new Date().getFullYear();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <footer className="w-full bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 mt-16">
      <div className="container mx-auto px-4 py-12">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Logo and site info */}
          <motion.div variants={itemVariants} className="flex flex-col items-center md:items-start">
            <Link href="/" className="flex items-center mb-4">
              <div className="relative w-[120px] h-12 overflow-hidden">
                <Image
                  src="/downloadmod_logo.png"
                  alt="downloadmod.com"
                  fill
                  className="object-contain w-full h-full"
                  sizes="120px"
                  priority
                  unoptimized
                />
              </div>
              {/* Fallback text nếu muốn, có thể thêm onError cho Image để hiện text nếu ảnh lỗi */}
            </Link>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center md:text-left">
              {t('siteSlogan')}
            </p>
            
            {/* Social links */}
            <div className="flex space-x-4 mt-2">
              {SOCIAL_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
                  aria-label={link.label}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </motion.div>
          
          {/* Quick links */}
          <motion.div variants={itemVariants} className="flex flex-col items-center md:items-start">
            <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Quick Links</h3>
            <nav className="flex flex-col space-y-2">
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                >
                  {t(link.key)}
                </Link>
              ))}
            </nav>
          </motion.div>
          
          {/* Language switcher */}
          <motion.div variants={itemVariants} className="flex flex-col items-center md:items-start">
            <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Language</h3>
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <LanguageSwitcher />
            </div>
          </motion.div>
        </motion.div>
        
        {/* Copyright */}
        <motion.div 
          className="mt-12 pt-4 border-t border-gray-200 dark:border-gray-800 text-center text-sm text-gray-500 dark:text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <p className="flex items-center justify-center">
            © {currentYear} DownloadMod. {t('allRightsReserved')}
          </p>
          <p className="mt-2 flex items-center justify-center">
            Made with <Heart className="w-4 h-4 mx-1 text-red-500" /> by DownloadMod Team
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
